/**
 * Sprint 80 — Una sola regla para "¿este producto está agotado?".
 *
 * Existe porque la misma regla vivía escrita en tres pantallas y sólo se corrigió en una.
 *
 * El Sprint 62 arregló en `MenuGrid` que un producto **sin control de inventario** no puede estar
 * agotado —dejó hasta el comentario explicándolo— pero `PosPage` se quedó con la versión vieja
 * (`stockEfectivo ?? inventarioActual`). `inventarioActual` es un campo muerto desde ese mismo
 * sprint y vale **0** en todo producto nuevo, así que el resultado era éste:
 *
 *   - la tarjeta del menú se veía normal y se dejaba tocar (MenuGrid, arreglado),
 *   - y al tocarla salía **"Sin stock — ya no hay más Coca Light disponible"** (PosPage, viejo).
 *
 * La dueña acababa de dar de alta sus bebidas y **ninguna se podía vender**. Lo encontró ella,
 * en su primera venta, con el cliente esperando.
 *
 * La regla, en una frase: **si el producto no lleva control de existencias, nunca está agotado.**
 * Quien quiera control lo vincula a un insumo; ése es el único modelo vivo desde el Sprint 62.
 */

/** Lo mínimo que hace falta para decidir. Cualquier vista del producto lo cumple. */
export interface ProductoConStock {
  /** Existencia derivada del insumo. `null`/`undefined` = el producto no lleva control. */
  stockEfectivo?: number | null;
  /** Mínimo para el semáforo, mismo criterio. */
  stockMinimoEfectivo?: number | null;
}

/**
 * Cuánto queda, o `null` si este producto no lleva inventario.
 *
 * **Nunca** cae a `inventarioActual`: ese campo quedó deprecado en el Sprint 62 y su 0 es lo que
 * hacía invendibles a los productos nuevos.
 */
export function stockDisponible(p: ProductoConStock): number | null {
  return p.stockEfectivo ?? null;
}

/** ¿Se acabó? Sólo puede acabarse lo que se cuenta. */
export function estaAgotado(p: ProductoConStock): boolean {
  const stock = stockDisponible(p);
  return stock !== null && stock <= 0;
}

/** ¿Va quedando poco? Para el semáforo del menú. */
export function estaBajo(p: ProductoConStock): boolean {
  const stock = stockDisponible(p);
  const minimo = p.stockMinimoEfectivo ?? null;
  return stock !== null && minimo !== null && minimo > 0 && stock <= minimo && stock > 0;
}

/**
 * El tope para el botón "+" del carrito: cuántos se pueden llegar a agregar.
 *
 * `null` significa **sin tope**, no cero. Confundir las dos cosas es la misma familia de error:
 * un producto sin inventario no puede tener un límite de cero.
 */
export function topeParaCarrito(p: ProductoConStock | undefined): number | null {
  if (!p) return null;
  return stockDisponible(p);
}
